// Компонент для ввода маркировочных кодов (КМ)
// Последовательный ввод: одно поле → Enter → следующее

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Chip,
    Alert,
    Divider,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    LinearProgress
} from '@mui/material';
import {
    QrCode as QrCodeIcon,
    Check as CheckIcon,
    Warning as WarningIcon,
    DeleteSweep as DeleteSweepIcon
} from '@mui/icons-material';
import { TableRowData } from './types';
import { calculateMarkCodesCount, createMarkCodesArray } from './utils';
import { convertRuToEn } from './keyboardMapping';
import { IconButton, Tooltip } from '@mui/material';

const MARK_CODE_LENGTH = 31;

// GS1 regex: "01" + 14 цифр (GTIN) + "21" + остаток (буквы/цифры латиницы)
const GS1_REGEX = /^01\d{14}21[0-9A-Za-z]+$/;

// Проверки на запрещённое содержимое
const FORBIDDEN_PATTERNS = {
    url: /https?:\/\/|www\./i,
    cyrillic: /[а-яА-ЯёЁ]/,
    spaces: /\s/,
    invalidChars: /[^0-9A-Za-z]/,
};

function validateGS1(code: string): { valid: boolean; error: string } {
    if (!code) return { valid: true, error: '' };

    // Проверяем запрещённые паттерны
    if (FORBIDDEN_PATTERNS.url.test(code)) {
        //return { valid: false, error: 'Недопустимое значение: содержит URL' };
        return { valid: false, error: 'Код не соответствует формату GS1: Недопустимое значение: содержит URL' };
    }
    if (FORBIDDEN_PATTERNS.cyrillic.test(code)) {
        //return { valid: false, error: 'Кириллица запрещена. Переключите раскладку на EN' };
        return { valid: false, error: 'Код не соответствует формату GS1: Кириллица запрещена. Переключите раскладку на EN' };
    }
    if (FORBIDDEN_PATTERNS.spaces.test(code)) {
        //return { valid: false, error: 'Пробелы запрещены в маркировочном коде' };
        return { valid: false, error: 'Код не соответствует формату GS1: Пробелы запрещены в маркировочном коде' };
    }
    if (FORBIDDEN_PATTERNS.invalidChars.test(code)) {
        //return { valid: false, error: 'Допустимы только цифры и латинские буквы (0-9, A-Z, a-z)' };
        return { valid: false, error: 'Код не соответствует формату GS1: Допустимы только цифры и латинские буквы (0-9, A-Z, a-z)' };
    }

    // Проверяем GS1 структуру только на полностью введённом коде
    if (code.length === MARK_CODE_LENGTH) {
        if (!code.startsWith('01')) {
            //return { valid: false, error: 'Код должен начинаться с "01" (Application Identifier)' };
            return { valid: false, error: 'Код не соответствует формату GS1: Код должен начинаться с "01"' };
        }
        // Проверяем 14 цифр GTIN после "01"
        const gtin = code.substring(2, 16);
        if (!/^\d{14}$/.test(gtin)) {
            //return { valid: false, error: 'Позиции 3-16 должны содержать 14 цифр (GTIN)' };
            return { valid: false, error: 'Код не соответствует формату GS1: Позиции 3-16 должны содержать 14 цифр (GTIN)' };
        }
        if (code.substring(16, 18) !== '21') {
            //return { valid: false, error: 'После GTIN должен идти идентификатор "21" (серийный номер)' };
            return { valid: false, error: 'Код не соответствует формату GS1: После GTIN должен идти идентификатор "21" (серийный номер)' };
        }
        if (!GS1_REGEX.test(code)) {
            return { valid: false, error: 'Код не соответствует формату GS1' };
        }
    }

    return { valid: true, error: '' };
}

interface MarkCodesInputProps {
    row: TableRowData;
    onUpdateTableRow: (id: string, field: keyof TableRowData, value: string) => void;
    isDisabled?: boolean;
    duplicateCodes?: Set<string>;
    hidePartialScanning?: boolean;
}

const MarkCodesInput: React.FC<MarkCodesInputProps> = ({
    row,
    onUpdateTableRow,
    isDisabled = false,
    duplicateCodes,
    hidePartialScanning = false
}) => {
    const quantity = parseFloat(row.Quantity) || 0;
    const neededCodesCount = calculateMarkCodesCount(
        row.Quantity,
        row.QuantityPcs,
        row.selectedMaterial?.PartCoeff
    );

    const [currentInput, setCurrentInput] = useState('');
    const [validationError, setValidationError] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    // инициализация массива маркировочных кодов
    useEffect(() => {
        const partCoeff = row.selectedMaterial?.PartCoeff;
        if (!row.markCodes) {
            const newMarkCodes = createMarkCodesArray(row.Quantity, [], row.QuantityPcs, partCoeff);
            onUpdateTableRow(row.id, 'markCodes', JSON.stringify(newMarkCodes));
        } else {
            const currentCodes = Array.isArray(row.markCodes) ? row.markCodes : JSON.parse(row.markCodes || '[]');
            const updatedCodes = createMarkCodesArray(row.Quantity, currentCodes, row.QuantityPcs, partCoeff);
            if (updatedCodes.length !== currentCodes.length) {
                onUpdateTableRow(row.id, 'markCodes', JSON.stringify(updatedCodes));
            }
        }
    }, [row.Quantity, row.QuantityPcs, row.selectedMaterial?.PartCoeff, row.id, row.markCodes, onUpdateTableRow]);

    const markCodes: string[] = useMemo(() => {
        if (!row.markCodes) return [];
        return Array.isArray(row.markCodes) ? row.markCodes : JSON.parse(row.markCodes || '[]');
    }, [row.markCodes]);

    const filledCodesCount = useMemo(() =>
        markCodes.filter((code: string) => code.length === MARK_CODE_LENGTH).length,
        [markCodes]
    );

    // Индекс следующего незаполненного кода
    const nextEmptyIndex = useMemo(() => {
        const idx = markCodes.findIndex((code: string) => code.length !== MARK_CODE_LENGTH);
        return idx >= 0 ? idx : -1;
    }, [markCodes]);

    // Конвертация раскладки (без обрезки — полный код от сканера)
    const convertInput = useCallback((value: string): string => {
        if (!value) return '';
        return convertRuToEn(value);
    }, []);

    // Валидация и обрезка до 31 символа (вызывается при подтверждении)
    const validateAndTrimCode = useCallback((value: string): { code: string; valid: boolean; error: string } => {
        if (!value) return { code: '', valid: false, error: '' };
        const converted = convertRuToEn(value);

        const trimmed = converted.slice(0, MARK_CODE_LENGTH);
        const gs1Result = validateGS1(trimmed);
        return { code: trimmed, valid: gs1Result.valid, error: gs1Result.error };
    }, []);

    // Сохранение кода и автопереход к следующему
    const commitCode = useCallback((index: number, value: string) => {
        const { code, valid, error } = validateAndTrimCode(value);
        if (!code || code.length !== MARK_CODE_LENGTH) {
            setValidationError('Код слишком короткий после обрезки');
            return false;
        }
        if (!valid) {
            setValidationError(error);
            return false;
        }
        const updatedCodes = [...markCodes];
        updatedCodes[index] = code;
        onUpdateTableRow(row.id, 'markCodes', JSON.stringify(updatedCodes));
        setValidationError('');
        return true;
    }, [markCodes, onUpdateTableRow, row.id, validateAndTrimCode]);

    // Обработка Enter — обрезать до 31, провалидировать, сохранить и перейти к следующему
    const handleSubmitCode = useCallback(() => {
        if (nextEmptyIndex < 0 || !currentInput.trim()) return;

        const { code, valid, error } = validateAndTrimCode(currentInput);
        if (!code || code.length !== MARK_CODE_LENGTH) {
            setValidationError('Код слишком короткий');
            return;
        }
        if (!valid) {
            setValidationError(error);
            return;
        }
        const updatedCodes = [...markCodes];
        updatedCodes[nextEmptyIndex] = code;
        onUpdateTableRow(row.id, 'markCodes', JSON.stringify(updatedCodes));
        setCurrentInput('');
        setValidationError('');
    }, [currentInput, markCodes, nextEmptyIndex, onUpdateTableRow, row.id, validateAndTrimCode]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmitCode();
        }
    }, [handleSubmitCode]);

    // Ввод без автосохранения — только конвертация раскладки
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const converted = convertInput(raw);
        setCurrentInput(converted);
        setValidationError('');
    }, [convertInput]);

    // Удаление конкретного кода
    const removeCode = useCallback((index: number) => {
        const updatedCodes = [...markCodes];
        updatedCodes[index] = '';
        onUpdateTableRow(row.id, 'markCodes', JSON.stringify(updatedCodes));
    }, [markCodes, onUpdateTableRow, row.id]);

    // Если нет ни упаковок, ни штук — не показываем
    const quantityPcs = parseFloat(row.QuantityPcs || '0') || 0;
    if (quantity <= 0 && quantityPcs <= 0) {
        return null;
    }

    const allFilled = filledCodesCount === neededCodesCount && neededCodesCount > 0;
    const progress = neededCodesCount > 0 ? (filledCodesCount / neededCodesCount) * 100 : 0;

    return (
        <Box sx={{
            border: '1px solid',
            borderColor: allFilled ? 'success.light' : '#e0e0e0',
            borderRadius: 1,
            p: 2,
            bgcolor: allFilled ? 'success.50' : '#fafafa',
            transition: 'all 0.3s'
        }}>
            {/* Заголовок */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: allFilled ? 'success.main' : 'primary.main' }}>
                        {allFilled ? <CheckIcon fontSize="small" /> : <QrCodeIcon fontSize="small" />}
                        <Typography variant="subtitle2" fontWeight="bold">
                            Маркировка
                        </Typography>
                    </Box>

                    <Chip
                        label={`${filledCodesCount} / ${neededCodesCount}`}
                        size="small"
                        color={allFilled ? "success" : "default"}
                        variant={allFilled ? "filled" : "outlined"}
                        sx={{ fontWeight: 'bold', height: 24 }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {/* Частичное сканирование - скрывается для маркированного возврата */}
                    {!hidePartialScanning ? (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={row.partialScanning || false}
                                    onChange={(e) => onUpdateTableRow(row.id, 'partialScanning', e.target.checked as any)}
                                    size="small"
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="caption" color="text.secondary">
                                    Не требовать все коды
                                </Typography>
                            }
                            sx={{ mr: 0 }}
                        />
                    ) : (
                        <Chip
                            label="Все коды обязательны"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600 }}
                        />
                    )}

                    {filledCodesCount > 0 && (
                        <Tooltip title="Очистить все коды">
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => onUpdateTableRow(row.id, 'markCodes', '[]')}
                                sx={{ border: '1px solid', borderColor: 'error.light', padding: 1 }}
                            >
                                <DeleteSweepIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Прогресс-бар */}
            <LinearProgress
                variant="determinate"
                value={progress}
                color={allFilled ? "success" : "primary"}
                sx={{ mb: 2, height: 4, borderRadius: 2, bgcolor: 'grey.200' }}
            />

            {/* Поле ввода — одно, активное */}
            {!allFilled && nextEmptyIndex >= 0 && (
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        size="small"
                        inputRef={inputRef}
                        label={`Код ${nextEmptyIndex + 1} из ${neededCodesCount}`}
                        placeholder="Отсканируйте код и нажмите Enter"
                        value={currentInput}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        disabled={isDisabled}
                        autoFocus
                        error={!!validationError}
                        helperText={
                            validationError
                                ? validationError
                                : (currentInput.length > 0
                                    ? `${currentInput.length} символов → будет обрезан до ${MARK_CODE_LENGTH}`
                                    : '')
                        }
                        InputProps={{
                            style: {
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                            },
                            endAdornment: currentInput.length > 0 && (
                                <InputAdornment position="end">
                                    <Chip label="Enter" size="small" onClick={handleSubmitCode} sx={{ cursor: 'pointer' }} />
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                    borderWidth: 2
                                }
                            }
                        }}
                    />
                </Box>
            )}

            {/* Список уже введённых кодов */}
            {filledCodesCount > 0 && (
                <>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Введённые коды:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 200, overflow: 'auto' }}>
                        {markCodes.map((code: string, index: number) => {
                            if (code.length !== MARK_CODE_LENGTH) return null;
                            const isDuplicate = duplicateCodes?.has(code) || false;
                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        py: 0.5,
                                        px: 1,
                                        borderRadius: 1,
                                        bgcolor: isDuplicate ? '#fff5f5' : 'white',
                                        border: isDuplicate ? '2px solid' : '1px solid',
                                        borderColor: isDuplicate ? 'error.main' : 'success.light',
                                        animation: isDuplicate ? 'pulse-error 1.5s ease-in-out infinite' : 'none',
                                        '@keyframes pulse-error': {
                                            '0%, 100%': { borderColor: 'error.main' },
                                            '50%': { borderColor: 'error.light' },
                                        },
                                        '&:hover': { borderColor: 'error.light' }
                                    }}
                                >
                                    {isDuplicate
                                        ? <WarningIcon sx={{ fontSize: 14, color: 'error.main' }} />
                                        : <CheckIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                    }
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            flexGrow: 1,
                                            color: isDuplicate ? 'error.main' : 'text.primary',
                                            fontWeight: isDuplicate ? 'bold' : 'normal',
                                            letterSpacing: 0.3
                                        }}
                                    >
                                        №{index + 1}: {code} {isDuplicate && '⚠ ДУБЛЬ'}
                                    </Typography>
                                    {!isDisabled && (
                                        <Tooltip title="Удалить код">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => removeCode(index)}
                                                sx={{ p: 0.25 }}
                                            >
                                                <DeleteSweepIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default MarkCodesInput;